/** @jsxImportSource @emotion/react */
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Breadcrumb, Card, Typography, Button, Space, Table, Tag } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { css } from '@emotion/react';
import { commonStyles } from '../../styles/commonStyles';
import MainLayout from '../../components/common/MainLayout';
import { useGetPropertiesQuery } from '../../api/plantingEventsApi';
import type { Property } from '../../types/plantingEvents';
import { useSave } from '../../contexts/WorldContext';

const { Title, Text } = Typography;

const FarmPropertiesPage: React.FC = () => {
  const { saveId } = useParams<{ saveId: string }>();
  const { t } = useTranslation('crops');
  const save = useSave();
  const saveTitle = save.farmName || save.id.slice(0, 8);

  const {
    data: attributes = [],
    isLoading,
    isError,
  } = useGetPropertiesQuery('planting');

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: t('properties.columns.name'),
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: Property) => (
        <div>
          <Text strong>{text}</Text>
          <br />
          <Text type="secondary" css={css({ fontSize: '0.85em' })}>
            {t('details.properties.code', { code: record.code })}
          </Text>
        </div>
      ),
    },
    {
      title: t('details.scenario.dataType'),
      dataIndex: 'dataType',
      key: 'dataType',
      width: 120,
      render: (dataType: string) => {
        const colorMap: Record<string, string> = {
          integer: 'blue',
          float: 'cyan',
          text: 'green',
          boolean: 'purple',
        };
        return <Tag color={colorMap[dataType] || 'default'}>{dataType}</Tag>;
      },
    },
    {
      title: t('properties.columns.options'),
      dataIndex: 'options',
      key: 'options',
      render: (options: string[]) =>
        options && options.length > 0 ? (
          <div>
            {options.slice(0, 3).map((opt, idx) => (
              <Tag key={idx} css={css({ marginBottom: 4 })}>
                {opt}
              </Tag>
            ))}
            {options.length > 3 && <Text type="secondary">+{options.length - 3}</Text>}
          </div>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: t('properties.columns.unit'),
      dataIndex: 'unitId',
      key: 'unitId',
      width: 100,
      render: (unitId: number | null) => (unitId ? t('properties.unitId', { id: unitId }) : '—'),
    },
  ];

  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb
          items={[
            { title: <Link to="/">{t('nav.home')}</Link> },
            { title: <Link to={`/saves/${saveId}`}>{saveTitle}</Link> },
            { title: t('properties.title') },
          ]}
        />
      }
    >
      <Title level={2}>{t('properties.title')}</Title>

      <Card>
        <Space direction="vertical" size="large" css={commonStyles.fullWidth}>
          <div>
            <Title level={4}>{t('properties.measuredTitle')}</Title>
            <Text type="secondary">
              <Trans i18nKey="crops:properties.help" components={{ b: <strong /> }} />
            </Text>
          </div>

          {isLoading && <div>{t('common:states.loading')}</div>}
          {isError && <div>{t('properties.loadFailed')}</div>}

          {!isLoading && !isError && (
            <Table
              dataSource={attributes}
              columns={columns}
              rowKey="id"
              pagination={false}
              bordered
            />
          )}

          <div>
            <Title level={5}>{t('properties.howTo.title')}</Title>
            <ol css={css({ paddingLeft: 20 })}>
              <li>{t('properties.howTo.step1')}</li>
              <li>{t('properties.howTo.step2')}</li>
              <li>{t('properties.howTo.step3')}</li>
              <li>{t('properties.howTo.step4')}</li>
              <li>{t('properties.howTo.step5')}</li>
            </ol>
          </div>

          <Space>
            <Button type="default" onClick={() => window.history.back()}>
              {t('common:actions.back')}
            </Button>
            <Button type="primary" icon={<PlusOutlined />} disabled>
              {t('properties.bulkCreate')}
            </Button>
          </Space>
        </Space>
      </Card>
    </MainLayout>
  );
};

export default FarmPropertiesPage;
